import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { AnimatePresence } from 'framer-motion'
import { ArrowLineLeftIcon, ArrowLineRightIcon } from '@phosphor-icons/react'

import { showAnimation } from './helpers/animations'
import { useAuthentication } from '@pages/Authentication/context'
import { ExitIcon } from '@assets/svgs/ExitIcon'
import { WarningIcon } from '@assets/svgs/WarningIcon'
import { ReturnModal } from '@components/modals/ReturnModal'
import { Tooltip } from '@components/shared/Tooltip'
import { MenuItens } from './components/MenuItens'
import { useLayout } from '@hooks/useLayout'

import type { SideBarProps } from './types'

import logoSodexoWhite from '@assets/svgs/logoSodexoWhite.svg'

import * as S from './styles'

export const SideBar = ({ sideBarCollapsed, setSideBarCollapsed }: SideBarProps) => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { sideBarData, isSideBarDataLoading, getSideBarData } = useLayout()
  const { signOut, isSignedOutLoading } = useAuthentication()

  const [hasMounted, setHasMounted] = useState(false)
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false)

  useEffect(() => {
    setHasMounted(true)
    getSideBarData()
  }, [])

  return (
    <>
      <AnimatePresence>
        <S.Drawer isOpen={sideBarCollapsed}>
          <S.OpenMenuButton onClick={() => setSideBarCollapsed(!sideBarCollapsed)}>
            {!sideBarCollapsed ? <ArrowLineRightIcon weight="bold" /> : <ArrowLineLeftIcon weight="bold" />}
          </S.OpenMenuButton>

          {sideBarCollapsed && (
            <S.TopBox>
              <S.LogoBox
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.3 }}
              >
                <S.SodexoLogo src={logoSodexoWhite} alt="Logo Sodexo" />
                SmartOps
              </S.LogoBox>
            </S.TopBox>
          )}

          <S.MenusBox isOpen={sideBarCollapsed}>
            {!!sideBarData.length &&
              !isSideBarDataLoading &&
              sideBarData.map((parent) => (
                <MenuItens
                  key={`menu-${parent.id}`}
                  parent={parent}
                  sideBarCollapsed={sideBarCollapsed}
                  pathname={pathname}
                  navigate={navigate}
                  hasMounted={hasMounted}
                />
              ))}
          </S.MenusBox>

          <S.BottomBox onClick={() => setIsLogoutModalOpen(true)}>
            <Tooltip title={!sideBarCollapsed ? 'Sair' : ''} placement="right">
              <S.ExitButton isOpen={sideBarCollapsed}>
                <S.Icon isSelected={false} isOpen={sideBarCollapsed}>
                  <ExitIcon />
                </S.Icon>

                {sideBarCollapsed && (
                  <S.MenuText
                    variants={showAnimation}
                    initial={hasMounted ? 'hidden' : false}
                    animate="show"
                    exit="hidden"
                  >
                    Sair
                  </S.MenuText>
                )}
              </S.ExitButton>
            </Tooltip>
          </S.BottomBox>
        </S.Drawer>
      </AnimatePresence>

      <ReturnModal
        open={isLogoutModalOpen}
        headerIcon={<WarningIcon />}
        title="Deseja realmente sair da plataforma?"
        message="Você terá que realizar o login novamente!"
        headerTitle="Você tem certeza?"
        cancelButtonText="Cancelar"
        confirmButtonText="Confirmar"
        onClose={setIsLogoutModalOpen}
        onConfirm={signOut}
        isLoading={isSignedOutLoading}
      />
    </>
  )
}
